import React, { type FC, useContext, useState } from 'react';

import { compose } from 'redux';

import { reloadCompany } from 'common/actions/company';
import AJAX from 'common/AJAX';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { ShowToastContext, ToastTypes } from 'common/containers/ToastContainer';
import connect from 'common/core/connect';
import Helmet from 'common/helmets/Helmet';
import Button from 'common/inputs/Button';
import UpsellModal from 'common/modals/UpsellModal';
import withAccessControl from 'common/routing/withAccessControl';
import SwitchV2 from 'common/ui/SwitchV2';
import { H2, P } from 'common/ui/Text';
import { CoreMonthlyPlanID } from 'common/util/isCore';
import parseAPIResponse, { isDefaultSuccessResponse } from 'common/util/parseAPIResponse';
import { RoutePermissions, testEveryPermission } from 'common/util/permissions';

import type { Company } from 'common/api/endpoints/companies';

import 'css/components/subdomain/admin/_AdminGeneralPreferencesSettings.scss';

type OwnProps = Record<string, never>;

type ConnectProps = {
  reloadCompany: () => void;
};

type Props = OwnProps & ConnectProps;

const AdminGeneralPreferencesSettings: FC<Props> = ({ reloadCompany }: Props) => {
  const company = useContext<Company>(CompanyContext);
  const [enableGlobalPostSearch, setGlobalSearch] = useState<boolean>(
    company.enableGlobalPostSearch,
  );
  const [enableTranslations, setEnableTranslations] = useState<boolean>(company.enableTranslations);

  const [loading, setLoading] = useState<boolean>(false);
  const showToast = useContext(ShowToastContext);
  const [showUpsellModal, setShowUpsellModal] = useState(false);

  const savePreferences = async () => {
    setLoading(true);

    const response = await AJAX.post('/api/company/changeSettings', {
      enableGlobalPostSearch,
      enableTranslations,
    });

    setLoading(false);

    const { error } = parseAPIResponse(response, {
      isSuccessful: isDefaultSuccessResponse,
    });
    if (error) {
      showToast(error.message, ToastTypes.error);
      return;
    }

    reloadCompany();
  };

  return (
    <div className="adminGeneralPreferencesSettings">
      <Helmet title="Preferences | General settings | Canny" />
      <section className="section">
        <H2 variant="headingMd">Board search results</H2>
        <div className="toggleContainer">
          <P className="value">Show posts from all boards when searching</P>
          <SwitchV2
            onChange={() => setGlobalSearch((enableGlobalPostSearch) => !enableGlobalPostSearch)}
            checked={enableGlobalPostSearch}
          />
        </div>
      </section>
      <section className="section">
        <H2 variant="headingMd">Translations</H2>
        <P className="text">
          Allow users to read user generated content in their language of choice.{' '}
        </P>
        <P className="text linkOut">
          <a
            href="https://help.canny.io/en/articles/9091829-beta-language-translations"
            target="_blank"
            rel="nofollow noopener">
            Learn more about translations
          </a>
        </P>
        <div className="toggleContainer">
          <P className="value">Allow auto-generated translations</P>
          <SwitchV2
            onChange={
              company.features?.translateContent
                ? setEnableTranslations
                : () => setShowUpsellModal(true)
            }
            checked={enableTranslations && company.features?.translateContent}
          />
        </div>
      </section>
      <UpsellModal
        cta="Drive engagement by offering content in your users' preferred language!"
        feature="translateContent"
        onClose={() => setShowUpsellModal(false)}
        onUpsell={() => setShowUpsellModal(false)}
        show={showUpsellModal}
        planID={CoreMonthlyPlanID}
      />
      <Button
        buttonType="cannyButton"
        className="saveButton"
        disabled={
          enableGlobalPostSearch === company.enableGlobalPostSearch &&
          enableTranslations === company.enableTranslations
        }
        loading={loading}
        onTap={savePreferences}
        value="Save preferences"
      />
    </div>
  );
};

// TODO: remove cast once `connect` is typed
export default compose(
  connect(null, { reloadCompany }),
  withAccessControl<Props>(
    testEveryPermission(RoutePermissions.adminSettings.general.preferences),
    '/admin/settings',
  ),
)(AdminGeneralPreferencesSettings) as unknown as React.FC<OwnProps>;
